"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { useI18n } from "@/context/i18n"
import {
  Map,
  Users,
  Zap,
  GraduationCap,
  Briefcase,
  School,
  BookOpen,
  Baby,
  Monitor,
  Globe,
  PartyPopper,
  Stamp,
  Stars,
  Church,
} from "lucide-react"
import { usePathname } from "next/navigation"

type Item = {
  label: string
  href: string
  icon: React.ElementType
}

type Props = {
  className?: string
  itemsTravel?: Item[]
}

export default function SecondaryServicesNav({ className, itemsTravel }: Props) {
  const { lang, t } = useI18n()
  const [fixed, setFixed] = useState(false)
  const [barHeight, setBarHeight] = useState(0)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const barRef = useRef<HTMLDivElement | null>(null)
  const pathname = usePathname()
  const STORAGE_KEY = 'secondary_services_last_category'
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const getTopOffset = () => {
    if (typeof window === 'undefined') return 64
    const lg = window.matchMedia('(min-width: 1024px)').matches
    const md = window.matchMedia('(min-width: 768px)').matches
    return lg ? 80 : md ? 72 : 64
  }

  useEffect(() => {
    const onScroll = () => {
      if (!wrapRef.current) return
      const top = wrapRef.current.getBoundingClientRect().top
      const shouldFix = top <= getTopOffset()
      if (shouldFix !== fixed) setFixed(shouldFix)
    }
    const onResize = () => {
      if (barRef.current) setBarHeight(barRef.current.offsetHeight)
    }
    onResize()
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [fixed])

  // canonical list of service entries (use translation keys for labels)
  type ServiceKey = { key: string; href: string; icon: React.ElementType; labelKey?: string }

  const serviceKeyList = useMemo<ServiceKey[]>(() => ([
    // Link Education (order: Grupos Educativos Internacionales, Idiomas, Estudia y trabaja, High school, Au Pair, Pathway, Educación Superior, Inglés online)
    { key: 'lets_study_children_educational_groups', href: '/servicios/grupos-educativos', icon: Users },
    { key: 'lets_study_children_languages', href: '/servicios/idiomas', icon: BookOpen },
    { key: 'lets_study_children_work_and_study', href: '/servicios/estudia-y-trabaja', icon: Briefcase },
    { key: 'lets_study_children_highschool', href: '/servicios/highschool', icon: School },
    { key: 'lets_study_children_aupair', href: '/servicios/aupair', icon: Baby },
    { key: 'lets_study_children_pathway', href: '/servicios/pathway', icon: GraduationCap },
    { key: 'lets_study_children_higher_education', href: '/servicios/educacion-superior', icon: GraduationCap },
    { key: 'lets_study_children_online_english', href: '/servicios/ingles-online', icon: Monitor },
    { key: 'lets_study_children_visas', labelKey: 'lets_study_children_visas', href: '/servicios/visas', icon: Stamp },

    // Link Travel (order: Viajes internacionales, Viajes nacionales, Despedida de solteros, Lunas de Miel, Cruceros, Quinceañeras, Bodas, Viajes Personalizados)
    { key: 'lets_travel_children_international_travel', href: '/servicios/viajes-internacionales', icon: Globe },
    { key: 'lets_travel_children_domestic_travel', href: '/servicios/viajes-nacionales', icon: Map },
    { key: 'lets_travel_children_bachelor_parties', href: '/servicios/despedida-de-solteros', icon: PartyPopper },
    { key: 'lets_travel_children_honeymoons', href: '/servicios/lunas-de-miel', icon: Stars },
    { key: 'lets_travel_children_cruises', href: '/servicios/cruceros', icon: Globe },
    { key: 'lets_travel_children_quinceaneras', href: '/servicios/quinceaneras', icon: PartyPopper },
    { key: 'lets_travel_children_destination_weddings', href: '/servicios/bodas', icon: Church },
    { key: 'lets_travel_children_personalized_trips', href: '/servicios/viajes-personalizados', icon: Map },
    { key: 'lets_travel_children_visas', labelKey: 'lets_study_children_visas', href: '/servicios/visas', icon: Stamp },
  ]), [])

  // group into categories by key prefix
  const categories = useMemo(() => {
    const groups: Record<string, typeof serviceKeyList> = { study: [], travel: [], other: [] }
    for (const s of serviceKeyList) {
      if (s.key.startsWith('lets_study')) groups.study.push(s)
      else if (s.key.startsWith('lets_travel')) groups.travel.push(s)
      else groups.other.push(s)
    }
    return groups
  }, [serviceKeyList])

  // map to Items with translated labels
  const mapToItems = (arr: ServiceKey[]) => arr.map(s => ({ label: t((s as ServiceKey).labelKey ?? s.key), href: s.href, icon: s.icon }))

  // determine current list to render
  const [current, setCurrent] = useState<Item[]>(() => {
    if (itemsTravel && itemsTravel.length > 0) return itemsTravel.slice(0, 12)
    // default to study on initial render; will be reconciled in effect
    return mapToItems(categories.study).slice(0, 12)
  })

  // helper to infer category from a href or pathname
  const inferCategoryFromPath = (path?: string | null) => {
    if (!path) return null
    const found = serviceKeyList.find(s => path === s.href || path.startsWith(s.href))
    if (!found) return null
    if (found.key.startsWith('lets_study')) return 'study'
    if (found.key.startsWith('lets_travel')) return 'travel'
    return 'other'
  }

  // sync current list when pathname or itemsTravel changes
  useEffect(() => {
    if (itemsTravel && itemsTravel.length > 0) {
      setCurrent(itemsTravel.slice(0, 12))
      return
    }

    // 1) If pathname corresponds to a service, use its category
    const catFromPath = inferCategoryFromPath(pathname || null)
    if (catFromPath) {
      setSelectedCategory(catFromPath)
      const items = catFromPath === 'study' ? mapToItems(categories.study) : catFromPath === 'travel' ? mapToItems(categories.travel) : mapToItems(categories.other)
      setCurrent(items.slice(0, 12))
      // persist last category
      try { localStorage.setItem(STORAGE_KEY, catFromPath) } catch (e) { /* ignore */ }
      return
    }

    // 2) If there's a persisted category, use it
    try {
      const persisted = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
      if (persisted && (persisted === 'study' || persisted === 'travel' || persisted === 'other')) {
        setSelectedCategory(persisted)
        const items = persisted === 'study' ? mapToItems(categories.study) : persisted === 'travel' ? mapToItems(categories.travel) : mapToItems(categories.other)
        setCurrent(items.slice(0, 12))
        return
      }
    } catch (e) { /* ignore */ }

    // 3) fallback to study
    setSelectedCategory('study')
    setCurrent(mapToItems(categories.study).slice(0, 12))
  }, [itemsTravel, pathname, categories, t])

  // listen to HeroSearch selection events (emitted on home hero tabs)
  useEffect(() => {
    const handler = (ev: Event) => {
      try {
        const detail = (ev as CustomEvent).detail as { key?: string }
        if (!detail || !detail.key) return
        const key = detail.key as string
        let cat: string | null = null
        if (key.startsWith('lets-study')) cat = 'study'
        else if (key.startsWith('lets-travel')) cat = 'travel'
        else if (key.startsWith('lets-inmigrate') || key.startsWith('lets-participaciones')) cat = 'other'
        if (!cat) return
        setSelectedCategory(cat)
        const items = cat === 'study' ? mapToItems(categories.study) : cat === 'travel' ? mapToItems(categories.travel) : mapToItems(categories.other)
        setCurrent(items.slice(0, 12))
        try { localStorage.setItem(STORAGE_KEY, cat) } catch (e) { /* ignore */ }
      } catch (e) {
        // ignore malformed events
      }
    }
    window.addEventListener('home-tour:select-category', handler as EventListener)
    return () => window.removeEventListener('home-tour:select-category', handler as EventListener)
  }, [categories])

  return (
    <div ref={wrapRef} className={cn("relative hidden md:block show-on-17in", className)}>
      {fixed && <div style={{ height: barHeight }} aria-hidden="true" />}
      <div
        ref={barRef}
        className={cn(
          "z-40 w-full border-b shadow-sm backdrop-blur-md",
          fixed ? "bg-black/85 text-white border-black/40 fixed left-0 right-0" : "bg-white/60 text-black border-gray-200 sticky top-16 md:top-[72px] lg:top-20"
        )}
        style={fixed ? { top: getTopOffset() } as React.CSSProperties : undefined}
        role="navigation"
        aria-label={t('secondary_services_nav_aria')}
      >
        <div className="container mx-auto px-3 md:px-6 py-1">
          <div className="flex items-center justify-between w-full">
            {current.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                className="group inline-flex flex-col items-center justify-center px-1 py-1.5 flex-1 text-center transition-colors hover:text-[#fb0009] min-w-0"
                title={label}
                data-ga-action="secondary_nav_click"
                data-ga-category="navigation"
                data-ga-label={label}
              >
                <Icon className={cn("w-4 h-4 group-hover:text-[#fb0009]", fixed ? "text-white/90" : "text-black/90")} />
                <span className={cn("mt-1 text-[9px] md:text-[10px] font-semibold group-hover:text-[#fb0009] normal-case text-center leading-tight whitespace-normal break-words max-w-full", fixed ? "text-white" : "text-black")}>
                  {label}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Scoped styles to show the secondary nav only on screens >= 17in (~1632px)
export const __secondary_services_nav_styles = (
  <style jsx>{`
    .show-on-17in { display: none !important; }
    @media (min-width: 1632px) {
      .show-on-17in { display: block !important; }
    }
  `}</style>
)
